import { TrashIcon, XIcon } from '@phosphor-icons/react'
import { useEffect, useId, useRef, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { ObjectId } from 'lib/ObjectId'
import type { ReplyOption, WaitForKeywordNodeData, WaitUnit } from 'app/types/workflows'
import { parseKeywords } from 'app/lib/workflows/replyKeywords'
import { type Panel } from './panelShared'
import { ChoiceSelect, Field, TextField, ToggleField } from './fields'
import { useWorkflowActions } from '../WorkflowActions'
import { Section } from './Section'
import { DURATION_INPUT, waitUnitChoices } from './panelShared'

/** Replies to a text, forked on their words: one exit per reply option, then Other reply and No reply. */
export function WaitForKeywordPanel({ data, set, errors }: Panel<WaitForKeywordNodeData>) {
  const hasDeadline = data.within !== undefined
  const { takeSnapshot } = useWorkflowActions()
  return (
    <>
      <Section title="Reply options">
        <p className="text-xs text-subtle">
          Only an exact match takes an option&apos;s path. Any other reply takes Other reply.
        </p>
        {errors.options && <p className="text-xs text-red-600">{errors.options}</p>}
        {data.options.map((option, index) => (
          <ReplyOptionRow
            key={option.id}
            option={option}
            nameError={errors[`options.${index}.label`]}
            wordsError={errors[`options.${index}.keywords`]}
            removable={data.options.length > 1}
            onChange={(patch) =>
              set({ options: data.options.map((other) => (other.id === option.id ? { ...other, ...patch } : other)) })
            }
            // Removing an option removes its exit, so it is a canvas edit: undo restores the same id
            // and the steps wired to it ("Add the option back" would make a new id).
            onRemove={() => {
              takeSnapshot()
              set({ options: data.options.filter((other) => other.id !== option.id) })
            }}
          />
        ))}
        <Button
          variant="dashed"
          size="xs"
          onClick={() =>
            set({
              options: [...data.options, { id: ObjectId(), label: ``, keywords: [] }],
            })
          }
        >
          + Add option
        </Button>
      </Section>
      <Section>
        <ToggleField
          label="Reply deadline"
          info="Waits for the contact to reply to the text above. A reply that is one of an option's words takes that option's path, any other reply takes Other reply, and past the deadline No reply takes the last path. Off waits with no time limit. Place this after a Send SMS."
          checked={hasDeadline}
          onChange={(on) => set(on ? { within: data.within ?? 1, unit: data.unit ?? `days` } : { within: undefined })}
        />
        {hasDeadline && (
          <Field label="No reply after">
            <div className="flex items-center gap-2">
              <div className="w-16">
                <TextField
                  type="number"
                  min={1}
                  className={DURATION_INPUT}
                  value={data.within ?? 1}
                  onChange={(e) => set({ within: Math.max(1, Number(e.target.value) || 1) })}
                />
              </div>
              <ChoiceSelect
                className="w-28"
                value={data.unit ?? `days`}
                choices={waitUnitChoices(data.within ?? 1)}
                onChange={(v) => set({ unit: v as WaitUnit })}
              />
            </div>
          </Field>
        )}
      </Section>
      <Section title="If someone else steps in">
        <ToggleField
          label="Stop if a person takes over"
          hint="Ends the workflow, without sending the no-reply message, when someone takes the thread off the AI agent, a colleague replies by hand, or a later workflow takes over the conversation. A patient who opts out always stops it."
          checked={data.stopOnHumanTakeover !== false}
          onChange={(on) => set({ stopOnHumanTakeover: on ? undefined : false })}
        />
      </Section>
    </>
  )
}

/** One reply option: its name and the words that take its exit. */
function ReplyOptionRow({
  option,
  nameError,
  wordsError,
  removable,
  onChange,
  onRemove,
}: {
  option: ReplyOption
  nameError?: string
  wordsError?: string
  removable: boolean
  onChange: (patch: Partial<ReplyOption>) => void
  onRemove: () => void
}) {
  return (
    <div
      role="group"
      aria-label={option.label || `Unnamed option`}
      className="flex flex-col gap-2 rounded-md border border-default bg-surface p-2"
    >
      <div className="flex items-end gap-1.5">
        <div className="min-w-0 flex-1">
          <Field label="Name" htmlFor={`reply-option-${option.id}-name`} error={nameError}>
            <TextField
              id={`reply-option-${option.id}-name`}
              placeholder="For example Confirmed"
              value={option.label}
              invalid={!!nameError}
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </Field>
        </div>
        <Button variant="subtle" size="icon-sm" aria-label="Remove option" disabled={!removable} onClick={onRemove}>
          <TrashIcon size={14} weight="bold" />
        </Button>
      </div>
      <ReplyWordRows keywords={option.keywords} error={wordsError} onChange={(keywords) => onChange({ keywords })} />
    </div>
  )
}

/** The words that take one option's exit, a row each. Rows are keyed by their word, and the unsaved
 *  row by the empty string. */
function ReplyWordRows({
  keywords,
  error,
  onChange,
}: {
  keywords: string[]
  error?: string
  onChange: (keywords: string[]) => void
}) {
  // What the user is typing, only while they are typing, and only in the row being typed in.
  // Otherwise every row shows the saved list, so a change made elsewhere (undo, another node)
  // never lingers here to be written back.
  const [draft, setDraft] = useState<{ row: string; text: string } | null>(null)
  // A row the user added, or the one an option with no words always shows. It is not saved: it
  // becomes a word when it loses focus with text in it, and disappears when it does not.
  const [hasBlank, setHasBlank] = useState(false)
  const [focusAt, setFocusAt] = useState<number | null>(null)
  // Rows that left focus empty while a click was on its way, dropped once that click has landed.
  const [dropped, setDropped] = useState<string[]>([])
  const pendingDrop = useRef<{ row: string; cancel: () => void } | null>(null)
  const inputs = useRef<(HTMLInputElement | null)[]>([])
  const firstId = useId()
  const rows = hasBlank || !keywords.length ? [...keywords, ``] : keywords

  // Runs after the render that holds the row, which a direct focus() from the handler would beat.
  useEffect(() => {
    if (focusAt === null) return
    inputs.current[focusAt]?.focus()
    setFocusAt(null)
  }, [focusAt])

  // Applied from an effect so it sees the data the click that delayed it has already changed.
  useEffect(() => {
    if (!dropped.length) return
    setDropped([])
    setDraft((current) => (current && dropped.includes(current.row) ? null : current))
    if (dropped.includes(``)) setHasBlank(false)
    const remaining = keywords.filter((word) => !dropped.includes(word))
    if (remaining.length !== keywords.length) onChange(remaining)
  }, [dropped, keywords, onChange])

  useEffect(() => () => pendingDrop.current?.cancel(), [])

  /** Replaces the row's text with the words it parses to, then writes the list if it changed. */
  const commit = (row: string, text: string) => {
    const words = parseKeywords(text)
    const at = row ? keywords.indexOf(row) : keywords.length
    setDraft(null)
    if (!row) setHasBlank(false)
    if (at < 0) return { words, next: keywords }
    const replaced = row ? keywords.flatMap((word, index) => (index === at ? words : word)) : [...keywords, ...words]
    const next = [...new Set(replaced)]
    if (next.join(`,`) !== keywords.join(`,`)) onChange(next)
    return { words, next }
  }

  const remove = (row: string) => {
    setDraft(null)
    if (row) onChange(keywords.filter((word) => word !== row))
    else setHasBlank(false)
  }

  /** Mouse down on a button blurs the row first. A row that vanished then would shift the layout
   *  before mouse up, and the browser would never deliver the click. So an emptied row waits for the
   *  click, or for focus to move on to something else, and goes then. */
  const dropAfterClick = (row: string, leavingFor: EventTarget) => {
    const cancel = () => {
      document.removeEventListener(`click`, finish)
      document.removeEventListener(`focusin`, onFocusIn)
      if (pendingDrop.current?.cancel === cancel) pendingDrop.current = null
    }
    const finish = () => {
      cancel()
      setDropped((current) => [...current, row])
    }
    const onFocusIn = (event: FocusEvent) => {
      if (event.target !== leavingFor) finish()
    }
    document.addEventListener(`click`, finish)
    document.addEventListener(`focusin`, onFocusIn)
    pendingDrop.current = { row, cancel }
  }

  return (
    <Field label="Replies that mean this" htmlFor={firstId} error={error}>
      <ul aria-label="Replies that mean this" className="flex flex-col gap-1.5">
        {rows.map((word, index) => {
          const value = draft?.row === word ? draft.text : word
          return (
            <li key={word} className="flex items-center gap-1.5">
              <TextField
                ref={(input) => {
                  inputs.current[index] = input
                }}
                id={index === 0 ? firstId : undefined}
                aria-label={`Reply word ${index + 1}`}
                placeholder="For example C"
                value={value}
                invalid={!!error && (!keywords.length || error.startsWith(`${word} `))}
                onFocus={() => {
                  if (pendingDrop.current?.row === word) pendingDrop.current.cancel()
                  setDraft({ row: word, text: value })
                }}
                onChange={(e) => setDraft({ row: word, text: e.target.value })}
                onBlur={(e) => {
                  // An unsaved row keeps focus across the Enter that made it, so it has no draft.
                  const text = draft?.row === word ? draft.text : word ? null : ``
                  if (text === null) return
                  const typed = parseKeywords(text)
                  const vanishes = typed.every((typedWord) => typedWord !== word && keywords.includes(typedWord))
                  if (vanishes && e.relatedTarget) dropAfterClick(word, e.relatedTarget)
                  else commit(word, text)
                }}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return
                  if (e.key === `Enter`) {
                    e.preventDefault()
                    if (!parseKeywords(value).length) return
                    const { words, next } = commit(word, value)
                    if (index < rows.length - 1) {
                      setFocusAt(Math.min(index + words.length, next.length - 1))
                      return
                    }
                    setHasBlank(true)
                    setFocusAt(next.length)
                  } else if (e.key === `Backspace` && !e.repeat && !value && index > 0) {
                    e.preventDefault()
                    remove(word)
                    setFocusAt(index - 1)
                  }
                }}
              />
              <Button
                variant="subtle"
                size="icon-sm"
                aria-label="Remove word"
                disabled={!keywords.length}
                onClick={() => remove(word)}
              >
                <XIcon size={14} weight="bold" />
              </Button>
            </li>
          )
        })}
      </ul>
      <Button
        variant="dashed"
        size="xs"
        className="mt-1.5"
        onClick={() => {
          if (pendingDrop.current?.row === ``) pendingDrop.current.cancel()
          setHasBlank(true)
          setFocusAt(rows.length > keywords.length ? rows.length - 1 : keywords.length)
        }}
      >
        + Add word
      </Button>
    </Field>
  )
}
